import React, { createContext, useMemo, useEffect } from 'react'
import { useWebSocket, type WebSocketConnection } from './useWebSocket'
import type { WebSocketConfig } from './useWebSocket'

const WebSocketContext = createContext<WebSocketConnection | null>(null)

interface WebSocketProviderProps {
  children: React.ReactNode
  config?: Partial<WebSocketConfig>
  onConnectionChange?: (status: WebSocketConnection['connectionStatus']) => void
  onError?: (error: Error) => void
  onExecutiveData?: (data: SimplifiedSiteStatus[]) => void
}

// Импортируем тип из API
import type { SimplifiedSiteStatus } from '../../shared/api/executive-monitoring.api'

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ 
  children, 
  config = {},
  onConnectionChange,
  onError,
  onExecutiveData
}) => {
  const connection = useWebSocket(config)
  
  // Обработчики событий
  useEffect(() => {
    onConnectionChange?.(connection.connectionStatus)
  }, [connection.connectionStatus, onConnectionChange])

  useEffect(() => {
    if (connection.error) {
      onError?.(connection.error)
    }
  }, [connection.error, onError])

  useEffect(() => {
    if (connection.executiveData.length > 0) {
      onExecutiveData?.(connection.executiveData)
    }
  }, [connection.executiveData, onExecutiveData])

  // Проверяем, что WebSocket поддерживается
  const isWebSocketSupported = useMemo(() => {
    if (typeof window === 'undefined') return false
    return 'WebSocket' in window || 'MozWebSocket' in window
  }, [])

  if (!isWebSocketSupported) {
    console.warn('WebSocket is not supported in this browser. Real-time updates will not work.')
  }

  const contextValue = useMemo(() => connection, [connection])

  const isDev = import.meta.env.DEV

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
      {/* Индикатор состояния соединения для разработки */}
      {isDev && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 p-3 bg-black/80 text-white rounded-lg text-sm">
          <div className={`w-3 h-3 rounded-full ${
            connection.isConnected ? 'bg-green-500 animate-pulse' : 
            connection.connectionStatus === 'connecting' ? 'bg-yellow-500' : 
            'bg-red-500'
          }`} />
          <span className="font-mono">
            WS: {connection.isConnected ? 'Connected' : connection.connectionStatus}
            {connection.pingLatency && ` (${connection.pingLatency}ms)`}
          </span>
        </div>
      )}
    </WebSocketContext.Provider>
  )
}

// Экспорт контекста для ручного использования
export { WebSocketContext }